import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

import reportsTableData from "layouts/content/data/reportsTableData";
import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
} from "@mui/material";
import AlertBox from "components/Alert/alert";
import CloseIcon from "@mui/icons-material/Close";
import { getReportList } from "services/apiService";
import { reportStatus } from "services/apiService";

function ReportsManagement() {
  const [people, setPeople] = useState([]);
  const [page, setPage] = useState(1);

  const [alert, setAlert] = useState({
    open: false,
    type: "success", // "success" | "error" | "confirm"
    title: "",
    message: "",
  });
  const [open, setOpen] = useState(false);
  const [videoUrl, setVideoUrl] = useState("");
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [selectedVideoId, setSelectedVideoId] = useState(null);
  const [reportId, setReportId] = useState(null);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  useEffect(() => {
    getAllReports();
  }, []);

  const getAllReports = async () => {
    try {
      const response = await getReportList();
      if (response?.success) {
        setPeople(response?.data);
      }
    } catch (err) {
      console.error("Fetch users failed:", err);
    }
  };

  const handleView = async (url) => {
    setVideoUrl(url);
    handleOpen(true);
  };

  const handleReject = async (id, videoId) => {
    setSelectedVideoId(videoId);
    setReportId(id);
    setRejectDialogOpen(true);
  };

  const handleApprove = async (id, videoId) => {
    setSelectedVideoId(videoId);
    setReportId(id);
    setAlert({
      open: true,
      type: "confirm",
      title: "Confirm Approval",
      message: "Are you sure you want to approve this report?",
    });
  };

  const confirmApprove = async () => {
    try {
      const response = await reportStatus({
        videoId: selectedVideoId,
        status: "approved",
        reportId: reportId,
        rejectionReason: "",
      });

      if (response?.success) {
        setAlert({
          open: true,
          type: "success",
          title: "Approved!",
          message: response?.message || "Report approved successfully.",
        });
        getAllReports();
      } else {
        setAlert({
          open: true,
          type: "error",
          title: "Error",
          message: response?.message || "Failed to approve report.",
        });
      }
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: "Something went wrong during approval.",
      });
    } finally {
      setReportId(null);
      setSelectedVideoId(null);
    }
  };

  const submitRejection = async () => {
    if (!rejectReason.trim()) {
      setAlert({
        open: true,
        type: "error",
        title: "Missing Reason",
        message: "Please enter a reason for rejection.",
      });
      return;
    }

    try {
      const response = await reportStatus({
        videoId: selectedVideoId,
        status: "rejected",
        reportId: reportId,
        rejectionReason: rejectReason?.trim(),
      });
      if (response?.success) {
        setAlert({
          open: true,
          type: "success",
          title: "Rejected!",
          message: response?.message || "Report rejected successfully.",
        });
        getAllReports();
      } else {
        setAlert({
          open: true,
          type: "error",
          title: "Error",
          message: response?.message || "Failed to reject report.",
        });
      }
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: "Something went wrong while rejecting report.",
      });
    } finally {
      setRejectDialogOpen(false);
      setRejectReason("");
      setSelectedVideoId(null);
      setReportId(null);
    }
  };

  const { columns, rows } = useMemo(() => {
    return reportsTableData({
      peopleList: people,
      onView: handleView,
      onReject: handleReject,
      onApprove: handleApprove,
    });
  }, [people]);

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <AlertBox
        open={alert.open}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        onClose={() => setAlert({ ...alert, open: false })}
        onConfirm={async () => {
          confirmApprove();
        }}
      />

      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Reported Videos
                </MDTypography>
              </MDBox>
              <MDBox pt={3}>
                <DataTable
                  table={{ columns, rows }}
                  isSorted={false}
                  entriesPerPage={{ defaultValue: 10, entries: [10, 15, 20, 35, 50] }}
                  showTotalEntries={true}
                  noEndBorder
                  canSearch={true}
                  page={page}
                  onPageChange={setPage}
                  searchFn={(user, query) => {
                    const lowerQuery = query?.toLowerCase();
                    return (
                      user.uploaded?.props?.name?.toLowerCase().includes(lowerQuery) ||
                      user.reported?.props?.name?.toLowerCase().includes(lowerQuery) ||
                      user?.reason?.props?.children?.toLowerCase().includes(lowerQuery) ||
                      user?.status?.props?.children?.props?.badgeContent
                        .toLowerCase()
                        .includes(lowerQuery) ||
                      user?.reportedAt?.props?.children?.toLowerCase().includes(lowerQuery)
                    );
                  }}
                />
              </MDBox>
            </Card>
            <Dialog
              open={open}
              onClose={handleClose}
              maxWidth="md"
              fullWidth
              PaperProps={{
                sx: { borderRadius: 2 },
              }}
            >
              <DialogTitle>
                Video Preview
                <IconButton
                  aria-label="close"
                  onClick={handleClose}
                  sx={{
                    position: "absolute",
                    right: 8,
                    top: 8,
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>

              <DialogContent
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  padding: "10px",
                }}
              >
                <video
                  src={videoUrl}
                  controls
                  autoPlay
                  style={{
                    maxHeight: "70vh",
                    width: "auto",
                    borderRadius: "8px",
                  }}
                  disablePictureInPicture={true}
                />
              </DialogContent>
            </Dialog>

            <Dialog
              open={rejectDialogOpen}
              onClose={() => setRejectDialogOpen(false)}
              maxWidth="xs"
              fullWidth
            >
              <DialogTitle>Reject Reported Video</DialogTitle>
              <DialogContent>
                <TextField
                  autoFocus
                  fullWidth
                  multiline
                  margin="dense"
                  label="Reason for rejection"
                  placeholder="Enter the reason for rejecting this video..."
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={submitRejection}
                  sx={{
                    color: "beige",
                  }}
                >
                  Save
                </Button>
              </DialogActions>
            </Dialog>
          </Grid>
        </Grid>
      </MDBox>

      <Footer />
    </DashboardLayout>
  );
}

export default ReportsManagement;
